import {
  faArrowDownToBracket,
  faFileExport,
  faTag,
  faTrashCan,
  faUserPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  ContextMenu,
  ContextMenuItem,
  ContextMenuList,
  ContextMenuTrigger,
  Flex,
  Popover,
  PopoverContent,
  PopperTrigger,
  SquareButton,
  ThreeDotsIcon,
  Toast,
  Tooltip,
  useOpenModal,
} from '@modusign/design-system';
import {
  downloadFiles as downloadFilesApi,
  getGetDocumentListQueryKey,
  getGetDocumentQueryKey,
  getGetExternalDocumentCountQueryKey,
  useDeleteWorkspaceExternalDocuments,
} from '@modusign/openapi-client-external-document';
import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useHistory } from 'react-router';
import { useToggle } from 'react-use';

import GlobalLoadingSpinner from '@app/components/Loading/GlobalLoadingSpinner';
import { useExternalDocumentPermission } from '@app/features/externalDocument/hooks/useExternalDocumentPermission';
import useUpdateExternalDocumentLabels from '@app/features/externalDocument/hooks/useUpdateExternalDocumentLabels';
import LabelApplyModalPopover from '@app/features/label/components/LabelPopovers/LabelApplyPopover';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import useResponsiveValue from '@app/hooks/useResponsiveValue';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { useCurrentUserRole, useHasPublicWorkspace } from '@app/hooks/workspace';
import { downloadFileFromURL } from '@app/store/transport-layer/files';

import useExternalDocument from '../../hooks/useExternalDocument';
import DeleteExternalDocumentModal from '../DeleteExternalDocumentModal';
import ExternalDocumenttransferModal from '../ExternalDocumentTransferModal';
import SingleDocumentResourceRoleModal from '../ResourceRoleModal/SingleDocumentResourceRoleModal';

interface ExternalDocumentListItemMenuButtonProps {
  context: 'LIST_PAGE' | 'DETAIL_PAGE';
  externalDocument: {
    title: string;
    id: string;
  };
  onMutationSuccess?: () => void;
}

export default function ExternalDocumentListItemMenuButton({
  context,
  externalDocument: { title, id: documentId },
  onMutationSuccess,
}: ExternalDocumentListItemMenuButtonProps) {
  const history = useHistory();
  const openModal = useOpenModal();
  const workspaceId = useWorkspaceId();
  const { createErrorHandler } = useOpenErrorModal();
  const queryClient = useQueryClient();
  const { data: externalDocument, refetch } = useExternalDocument(documentId, {
    // 목록 뷰에서는 라벨 수정을 제공하지 않습니다.
    enabled: context === 'DETAIL_PAGE',
  });
  const hasPublicWorkspace = useHasPublicWorkspace();
  const role = useCurrentUserRole();
  const isMobileWidth = useResponsiveValue([true, false, false]);

  const [isMenuOpen, toggleMenu] = useToggle(false);

  const { deletePermission } = useExternalDocumentPermission(documentId, {
    enabled: isMenuOpen,
  });

  const [isApplyLabelPopoverOpen, setApplyLabelPopoverOpen] = useState(false);

  const [isDeleteModalOpen, toggleDeleteModal] = useToggle(false);

  const invalidate = () => {
    queryClient.invalidateQueries(getGetDocumentListQueryKey(workspaceId));
    queryClient.invalidateQueries(getGetExternalDocumentCountQueryKey(workspaceId));
    queryClient.removeQueries(getGetDocumentQueryKey(workspaceId, documentId));
  };

  const { mutate: deleteDocument, isLoading: isDeleting } = useDeleteWorkspaceExternalDocuments({
    mutation: {
      onSuccess: () => {
        toggleDeleteModal(false);
        Toast.success({ description: '1개의 계약을 삭제했습니다.' });
        invalidate();
        onMutationSuccess?.();
        if (context === 'DETAIL_PAGE') {
          history.push('/external-documents/list');
        }
      },
      onError: createErrorHandler({ title: '계약 삭제 실패' }),
    },
  });

  const { mutate: downloadFiles, isLoading: isDownloading } = useMutation(
    async () => {
      const { url } = await downloadFilesApi(workspaceId, documentId);
      downloadFileFromURL({ url, saveFileName: `${title}.zip` });
    },
    {
      onError: createErrorHandler({ title: '파일 다운로드 실패' }),
    }
  );

  const { mutateAsync: updateLabel } = useUpdateExternalDocumentLabels({ onSuccess: refetch });

  const isLoading = isDownloading;

  const isDocumentTransferSupported =
    !!hasPublicWorkspace && role?.type === 'ADMIN' && !isMobileWidth;

  return (
    <>
      {isLoading && <GlobalLoadingSpinner />}
      <Popover
        isOpen={isApplyLabelPopoverOpen}
        mode="click"
        placement="bottom-end"
        onOpenChange={(isOpen) => {
          // label apply popover는 메뉴에 click handler를 통해서만 엽니다.
          if (isOpen) {
            return;
          }
          setApplyLabelPopoverOpen(isOpen);
        }}
      >
        <PopperTrigger>
          <Box>
            <ContextMenu isOpen={isMenuOpen} onOpenChange={toggleMenu}>
              <ContextMenuTrigger>
                <SquareButton
                  aria-expanded={isMenuOpen}
                  aria-haspopup
                  aria-label="추가메뉴 토글하기"
                  onClick={toggleMenu}
                >
                  <ThreeDotsIcon />
                </SquareButton>
              </ContextMenuTrigger>
              <ContextMenuList left="0" role="menu" sx={{ width: '142px' }} top="34px">
                <ContextMenuItem
                  display="flex"
                  role="menuitem"
                  sx={{ alignItems: 'center' }}
                  onClick={() => {
                    downloadFiles();
                    toggleMenu();
                  }}
                >
                  <Box color="iconNeutralSlightlyDark" mr={2} width="12px">
                    <FontAwesomeIcon icon={faArrowDownToBracket} />
                  </Box>
                  다운로드
                </ContextMenuItem>
                {context === 'DETAIL_PAGE' && (
                  <ContextMenuItem
                    display="flex"
                    role="menuitem"
                    sx={{ alignItems: 'center' }}
                    onClick={() => {
                      toggleMenu();
                      setApplyLabelPopoverOpen(true);
                    }}
                  >
                    <Box color="iconNeutralSlightlyDark" mr={2} width="12px">
                      <FontAwesomeIcon icon={faTag} />
                    </Box>
                    라벨
                  </ContextMenuItem>
                )}
                <ContextMenuItem
                  display="flex"
                  role="menuitem"
                  sx={{ alignItems: 'center' }}
                  onClick={() => {
                    openModal(({ onClose }) => {
                      return (
                        <SingleDocumentResourceRoleModal
                          document={{ title, id: documentId }}
                          onClose={onClose}
                          onComplete={() => {
                            onClose();
                            onMutationSuccess?.();
                          }}
                        />
                      );
                    });
                    toggleMenu();
                  }}
                >
                  <Box color="iconNeutralSlightlyDark" mr={2} width="12px">
                    <FontAwesomeIcon icon={faUserPlus} />
                  </Box>
                  공유
                </ContextMenuItem>
                <ContextMenuItem
                  disabled={!deletePermission.isPermitted}
                  display="flex"
                  role="menuitem"
                  sx={{ alignItems: 'center' }}
                  onClick={() => {
                    toggleDeleteModal();
                    toggleMenu();
                  }}
                >
                  <Tooltip
                    content={deletePermission.message}
                    disabled={deletePermission.isPermitted}
                    placement="left"
                  >
                    <Flex>
                      <Box
                        color={
                          deletePermission.isPermitted
                            ? 'iconNeutralSlightlyDark'
                            : 'iconNeutralLight'
                        }
                        mr={2}
                        width="12px"
                      >
                        <FontAwesomeIcon icon={faTrashCan} />
                      </Box>
                      삭제
                    </Flex>
                  </Tooltip>
                </ContextMenuItem>
                {isDocumentTransferSupported && (
                  <>
                    <Box
                      as="li"
                      backgroundColor="dividerNeutralLight"
                      height="1px"
                      my={2}
                      role="separator"
                      width="100%"
                    />
                    <ContextMenuItem
                      display="flex"
                      role="menuitem"
                      sx={{ alignItems: 'center' }}
                      onClick={() => {
                        openModal(({ onClose }) => (
                          <ExternalDocumenttransferModal
                            documents={[{ title, id: documentId }]}
                            onCancel={onClose}
                            onComplete={() => {
                              onClose();
                              invalidate();
                              onMutationSuccess?.();
                              if (context === 'DETAIL_PAGE') {
                                history.push('/external-documents/list');
                              }
                            }}
                          />
                        ));
                        toggleMenu();
                      }}
                    >
                      <Box color="iconNeutralSlightlyDark" mr={2} width="12px">
                        <FontAwesomeIcon icon={faFileExport} />
                      </Box>
                      이전
                    </ContextMenuItem>
                  </>
                )}
              </ContextMenuList>
            </ContextMenu>
          </Box>
        </PopperTrigger>
        <PopoverContent>
          <LabelApplyModalPopover
            initialLabeled={Object.fromEntries(
              externalDocument?.labels.map((l) => [l.id, 'ALL']) || []
            )}
            onSubmit={(data) => {
              return updateLabel(
                {
                  workspaceId,
                  data: {
                    documentLabels: data.map((d) => ({
                      documentId,
                      labelId: d.labelId,
                      apply: d.apply,
                    })),
                  },
                },
                {
                  onSettled: () => {
                    setApplyLabelPopoverOpen(false);
                  },
                }
              );
            }}
          />
        </PopoverContent>
      </Popover>

      <DeleteExternalDocumentModal
        context="CONTEXT_MENU"
        documents={[{ title }]}
        isLoading={isDeleting}
        isOpen={isDeleteModalOpen}
        onCancel={toggleDeleteModal}
        onConfirm={() => deleteDocument({ workspaceId, data: { documentIds: [documentId] } })}
      />
    </>
  );
}
